import TextareaAutosize from 'react-textarea-autosize';
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, AlertTriangle, X } from 'lucide-react';
import { MicButton } from '@/features/chat/speech/MicButton';
import { VoiceRecordingBar } from '@/features/chat/speech/VoiceRecordingBar';
import { useSpeechInput } from '@/features/chat/speech/useSpeechInput';

interface MessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (value: string) => void;
  isLoading: boolean;
  disabled?: boolean;
  placeholder?: string;
}

export const MessageInput = ({
  value,
  onChange,
  onSend,
  isLoading,
  disabled = false,
  placeholder = "Type your message..."
}: MessageInputProps) => {
  const isDisabled = disabled || isLoading;
  const canSend = value.trim() && !isDisabled;

  const speech = useSpeechInput(value, onChange);
  const isIdle = speech.state === 'idle' || speech.state === 'error';

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend(value);
    }
  };

  // Stop-and-send: text goes straight to the agent. `speech.stop()` resolves
  // only after the session finishes finalizing, with the last turn already
  // committed, so this never fires on a partial/incomplete transcript.
  const handleVoiceSend = async () => {
    const finalText = await speech.stop();
    if (!finalText.trim()) return; // silence: the error banner already explains it
    if (isDisabled) return; // don't step on an in-flight send or an exhausted chat
    onSend(finalText);
  };

  return (
    <div data-tutorial="message-input" className="flex flex-col w-full gap-1 rounded-xl border p-2 md:p-4">
      {isIdle ? (
        // Same two-row shape as VoiceRecordingBar: the growing text gets its
        // own full-width row on top, controls (mic, send) get a row of their
        // own underneath — instead of sharing one row, which forced the text
        // into a narrow column and left it looking misaligned once it grew
        // past a line or two.
        <div className="flex flex-col gap-2 w-full">
          <TextareaAutosize
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            // iOS Safari auto-zooms the whole page on focus if an input's font-size is
            // under 16px — so this can't follow the usual "smaller on mobile" pattern;
            // text-base (16px) is required at every breakpoint to keep that from firing.
            className="w-full resize-none max-h-[200px] bg-background px-3 py-2 focus:outline-none text-base"
            minRows={1}
            disabled={isDisabled}
          />
          <div className="flex items-center justify-end gap-2 w-full">
            <MicButton isSupported={speech.isSupported} disabled={isDisabled} onStart={speech.start} />
            <Button
              onClick={() => onSend(value)}
              disabled={!canSend}
              size="icon"
              className="md:h-11 md:w-11 h-10 w-10 shrink-0 rounded-full cursor-pointer"
            >
              {isLoading ?
                <Loader2 className="h-4 w-4 animate-spin" /> :
                <ArrowLeft className="rotate-90 h-5 w-5" />
              }
            </Button>
          </div>
        </div>
      ) : (
        <VoiceRecordingBar
          state={speech.state}
          value={value}
          onCancel={speech.cancel}
          onSend={() => void handleVoiceSend()}
        />
      )}

      {speech.error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span className="flex-1">{speech.error.message}</span>
          <button
            type="button"
            onClick={speech.dismissError}
            aria-label="Dismiss"
            className="text-red-700 hover:text-red-900 cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};
